import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaSearch } from "react-icons/fa";
import profileimg from "../../assets/profile.png";
import newsfeed from "../../assets/images/icons/newsfeed.svg";
import friendsfollowers from "../../assets/images/icons/friendsandfollowers.svg";
import groupsandfamily from "../../assets/images/icons/groupandfamily.svg";
import chats from "../../assets/images/icons/chats.svg";
import notification from "../../assets/images/icons/notification.png";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { useLocation } from "react-router-dom";
import { useSearch } from "../../context/SearchContext";
import { IoIosNotificationsOutline } from "react-icons/io";
import Notification from "../common/Notification";
import MarketplaceCartLink from "../common/MarketplaceCartLink";
import { liveStreamPath } from "../marketplace/LiveShopColumn";
import { clearAllStorage } from "../../utils/auth";

import { fetchOwnProfile } from "../../utils/auth";
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");

// Link, image and text lines for one /search/global result in the search dropdown
const searchResultItem = (result) => {
  switch (result.type) {
    case "live_stream":
      return {
        to: liveStreamPath(result),
        image: result.profile_picture_url,
        title: result.title || `@${result.username} is live`,
        subtitle: `Live now · @${result.username}`,
      };
    case "group":
      return {
        to: `/groupsfamily?group=${result.id}`,
        image: result.group_image,
        title: result.group_name,
        subtitle: result.is_family_group ? "Family" : "Group",
      };
    case "seller":
      return {
        to: `/shopbuyeformarketplace?sellerId=${result.id}`,
        image: result.profile_image?.url,
        title: result.store_name || result.name,
        subtitle: "Store",
      };
    case "post":
      return {
        to: `/post/${result.id}`,
        image: result.profile_picture_url,
        title: result.title || result.content,
        subtitle: `Post by @${result.username}`,
      };
    case "product":
      return {
        to: `/ProductView/${result.id}`,
        image: result.product_media?.[0]?.url,
        title: result.product_name,
        subtitle: result.brand_name || "Product",
      };
    case "story":
      return {
        to: `/story/${result.id}`,
        image: result.profile_picture_url,
        title: result.content,
        subtitle: `Story by @${result.username}`,
      };
    default:
      return {
        to: `/profile/${result.username}`,
        image: result.profile_picture_url,
        title:
          result.account_type === "business"
            ? result.business_name
            : `${result.first_name || ""} ${result.last_name || ""}`.trim(),
        subtitle: `@${result.username}`,
      };
  }
};
export default function Headmenu() {
  const location = useLocation();

  // User states for email and mobile login
  const [userEmail, setUserEmail] = useState(null);
  const [userMobile, setUserMobile] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(() => {
    return sessionStorage.getItem("profilePictureUrl") || profileimg; // Default to placeholder image
  });
  const [coverPictureUrl, setCoverPictureUrl] = useState("");

  useEffect(() => {
    const handleProfilePictureUpdate = () => {
      const updatedProfilePictureUrl =
        sessionStorage.getItem("profilePictureUrl");
      if (updatedProfilePictureUrl) {
        setProfilePictureUrl(updatedProfilePictureUrl);
      }
    };

    // Add a custom event listener for profile picture updates
    window.addEventListener(
      "profilePictureUpdated",
      handleProfilePictureUpdate
    );

    // Reinitialize profilePictureUrl on component mount
    handleProfilePictureUpdate();

    // Cleanup the event listener
    return () => {
      window.removeEventListener(
        "profilePictureUpdated",
        handleProfilePictureUpdate
      );
    };
  }, []);
  // Token states
  const [tokenEmail, setTokenEmail] = useState(null);
  const [tokenMobile, setTokenMobile] = useState(null);

  useEffect(() => {
    // Email login session
    const storedUser = sessionStorage.getItem("user") || localStorage.getItem("user");
    const storedToken = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");

    if (storedUser) {
      setUserEmail(JSON.parse(storedUser));
    }
    if (storedToken) {
      setTokenEmail(storedToken);
    }
  }, []);

  useEffect(() => {
    // Retrieve the uploaded profile picture URL from sessionStorage
    const storedProfilePictureUrl = sessionStorage.getItem("profilePictureUrl");
    if (storedProfilePictureUrl) {
      setProfilePictureUrl(storedProfilePictureUrl);
    }
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      const token = sessionStorage.getItem("accessToken") || localStorage.getItem("accessToken");

      if (!token) {
        console.warn("Token not found");
        return;
      }

      try {
        const response = await fetchOwnProfile(token);

        const data = await response.json();

        if (response.ok && data.result) {
          // Save profile picture URL to sessionStorage and state
          const profilePic = data.result.profile_picture_url;
          const coverPic = data.result.cover_picture_url;

          if (profilePic) {
            sessionStorage.setItem("profilePictureUrl", profilePic);
            localStorage.setItem("profilePictureUrl", profilePic);
            setProfilePictureUrl(profilePic);
          }

          if (coverPic) {
            setCoverPictureUrl(coverPic);
          }

          // Optional: save other user details if needed
          setUserEmail(data.result);
        } else {
          console.error("Profile fetch error:", data.message);
        }
      } catch (err) {
        console.error("Network error fetching profile:", err);
      }
    };

    fetchUserProfile();
  }, []);
  // Search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState(null);

  // Search runs once typing pauses, and only the latest query's response is applied
  const searchTimerRef = useRef(null);
  const latestQueryRef = useRef("");
  useEffect(() => () => clearTimeout(searchTimerRef.current), []);

  // Function to perform API search
  const performSearch = async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    setError(null);

    try {
      // limit applies per result type (users, lives, groups, sellers, products, posts, stories)
      const params = new URLSearchParams({ query, limit: 3 });
      const response = await fetch(`${API_BASE_URL}/search/global?${params}`, {
        headers: { token: ` ${tokenEmail || tokenMobile}` },
      });

      const data = await response.json();
      if (latestQueryRef.current !== query) return;

      if (response.ok) {
        setSearchResults(data.result?.results || []);
      } else {
        setError(data.message || "Error fetching results");
      }
    } catch (err) {
      if (latestQueryRef.current !== query) return;
      setError("Network error, please try again.");
    }

    setIsSearching(false);
  };

  // Handle search input changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    latestQueryRef.current = query;
    clearTimeout(searchTimerRef.current);
    if (query.length < 3) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    searchTimerRef.current = setTimeout(() => performSearch(query), 300);
  };

  // The name/avatar block is hidden after scrolling 100px. It keeps its space (invisible, not display:none), because removing it
  // shrank the page and moved the scroll position, which made short pages jump when scrolling back up.
  const [isVisible, setIsVisible] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY <= 100);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
      <div className="">
        <div className="flex justify-between items-center mb-6 w-full">
          <div className="flex relative w-full">
            <div className="fasearch">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              placeholder="Search for people, communities or groups"
              className="search-input focus:outline-none focus:ring-2 focus:ring-gray-300 text-[#000] w-[]"
            />
            {searchQuery.length >= 3 && (
              <ul className="search-results absolute top-10 z-50 w-full bg-white shadow-lg rounded-lg mt-1 max-h-96 overflow-y-auto">
                {isSearching ? (
                  <li className="p-4 text-center">Searching...</li>
                ) : error ? (
                  <li className="p-4 text-center text-red-500">{error}</li>
                ) : searchResults.length > 0 ? (
                  searchResults.map((result) => {
                    const item = searchResultItem(result);
                    return (
                      <li key={`${result.type}-${result.id}`} className="hover:bg-gray-100">
                        <Link
                          to={item.to}
                          className="search-result-item flex items-center p-3"
                          onClick={() => {
                            clearTimeout(searchTimerRef.current);
                            latestQueryRef.current = "";
                            setSearchQuery("");
                            setSearchResults([]);
                          }}
                        >
                          <img
                            src={item.image || profileimg}
                            alt={item.title}
                            className="w-10 h-10 rounded-full mr-3"
                          />
                          <div className="min-w-0">
                            <div className="font-medium truncate">{item.title}</div>
                            <div className="text-sm text-gray-500 truncate">
                              {item.subtitle}
                            </div>
                          </div>
                        </Link>
                      </li>
                    );
                  })
                ) : (
                  <li className="p-4 text-center text-gray-500">
                    No results found
                  </li>
                )}
              </ul>
            )}
            {/* <div className="relative top-2 hidden lg:block">
              <img
                src={notification}
                alt="Notifications"
                className="ml-4 cursor-pointer"
              />
            </div> */}

            <div className="relative top-0 hidden lg:block">
              {/* <button
                type="button" yy
                className="relative inline-flex items-center p-1 text-sm font-medium text-center"
              >
                <IoIosNotificationsOutline className="text-[40px] text-gray-700 dark:text-white" />

                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-white dark:border-gray-900">
                  20
                </span>
              </button> */}
              <Notification />
            </div>
            <MarketplaceCartLink className="hidden lg:inline-flex ml-2" />
          </div>

          <div className="flex items-center hidden lg:block">
            <div
              className={`search-fixed-menu w-full p-4 pr-1 z-50 transition-all duration-300 ${isVisible ? "block" : "invisible opacity-0 pointer-events-none"
                }`}
            >
              <div className="flex items-center">
                <div className="text-right pr-2 w-max">
                  {userEmail ? (
                    <>
                      <p>{userEmail.first_name || "No Name"} {userEmail.last_name || "No Name"}</p>
                      <p>@{userEmail.username || "No Email"}</p>
                    </>
                  ) : userMobile ? (
                    <>
                      <p>{userMobile.first_name || "No Name"} {userMobile.last_name || "No Name"}</p>
                      <p>@{userMobile.username || "No Phone"}</p>
                    </>
                  ) : (
                    <>
                      <p>Proveen Krishnamoorthy</p>
                      <p>@proveen_sky</p>
                    </>
                  )}
                  <div className="ml-4">
                    {/* <button
                      className="text-sm text-[#E63946] hover:underline"
                      onClick={() => {
                        clearAllStorage();
                        navigate('/login');
                      }}
                    >
                      Logout
                    </button> */}
                  </div>
                </div>
                <div className="profile-dashboard rounded-full overflow-hidden">
                  {/* <img src={profilePictureUrl || profileimg} // Use uploaded profile picture or fallback */}
                  <img
                    src={profilePictureUrl || profileimg} // Use uploaded profile picture or fallback
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                </div>

              </div>
            </div>
          </div>
        </div>

        <div className="mobile-nav md:hidden mb-4 lg:hidden">
          <a
            href="/home"
            className={`aside-menu rounded-xl ${location.pathname === "/home" ? "active" : "hover:bg-gray-200"
              }`}
          >
            <img src={newsfeed} alt="News Feeds" />
            {location.pathname === "/home" && <span>News Feeds</span>}
          </a>

          <a
            href="/friendsfollowers"
            className={`aside-menu rounded-xl ${location.pathname === "/friendsfollowers"
                ? "active"
                : "hover:bg-gray-200"
              }`}
          >
            <img src={friendsfollowers} alt="Friends & Followers" />
            {location.pathname === "/friendsfollowers" && <span>Friends</span>}
          </a>

          <a
            href="/groupsfamily"
            className={`aside-menu rounded-xl ${location.pathname === "/groupsfamily"
                ? "active"
                : "hover:bg-gray-200"
              }`}
          >
            <img src={groupsandfamily} alt="Groups & Family" />
            {location.pathname === "/groupsfamily" && <span>Group</span>}
          </a>

          <a
            href="/chats"
            className={`aside-menu rounded-xl ${location.pathname === "/chats" ? "active" : "hover:bg-gray-200"
              }`}
          >
            <img src={chats} alt="Chats" />
            {location.pathname === "/chats" && <span>Chats</span>}
          </a>
        </div>
      </div>
    );
}
